import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Select } from '../../components/ui/Input';
import { Spinner } from '../../components/ui/Spinner';
import type { ContentVersion, OptimizationReport, WebsiteReviewTransparency } from '../../api/content';
import { useContentItem, useContentVersions, useDeleteModification, useGenerateWebsiteRefresh, usePatchContentItem, useRecalculateWebsiteReviewScore, useRefreshSerpEntities, useRestoreContentVersion, useRewriteModifications, useWebsiteReview } from '../../hooks/useContent';
import { useDeleteFeedbackRule } from '../../hooks/useFeedbackRules';
import { WebsiteReviewPanel } from './WebsiteReviewPanel';
import { VerificationPanel } from './VerificationPanel';
import { LinkedInReviewPanel } from './LinkedInReviewPanel';
import { TYPE_LABELS } from './content-labels';
import { getDisplayStatusLabel, getDisplayStatusVariant } from './content-status';
import { ReviewerPicker } from './ReviewerPicker';
import { RegenerateDialog } from './RegenerateDialog';
import { formatDateTime, formatDayMonthTime } from '../../utils/dateFormat';
import type { ReviewMeta } from './reviewers';
import { displayTitle } from './content-duplicates';

type WebsiteReviewStatus = 'draft' | 'review' | 'approved';

function normalizeWebsiteReviewStatus(status: string): WebsiteReviewStatus {
  if (status === 'draft' || status === 'review' || status === 'approved') {
    return status;
  }
  return 'approved';
}

function extractSeoKeywords(value: unknown): string[] {
  if (typeof value !== 'string') return [];
  return [...new Set(
    value
      .split(/[,;\n]/)
      .map((entry) => entry.trim())
      .filter(Boolean),
  )];
}

export function ContentReviewPage() {
  const { contentItemId } = useParams<{ contentItemId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const itemQuery = useContentItem(contentItemId ?? null);
  const versionsQuery = useContentVersions(contentItemId ?? null);
  const reviewQuery = useWebsiteReview(contentItemId ?? null);
  const modificationsQuery = useRewriteModifications(contentItemId ?? null);
  const patchItem = usePatchContentItem();
  const recalculateScore = useRecalculateWebsiteReviewScore();
  const refreshSerp = useRefreshSerpEntities();
  const generateRefresh = useGenerateWebsiteRefresh();
  const restoreVersion = useRestoreContentVersion();
  const [regenDialogOpen, setRegenDialogOpen] = useState(false);
  const [versionPreview, setVersionPreview] = useState<ContentVersion | null>(null);
  const [serpFeedback, setSerpFeedback] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const deleteFeedbackRule = useDeleteFeedbackRule();
  const deleteModification = useDeleteModification();
  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null);
  const [deletingModificationId, setDeletingModificationId] = useState<string | null>(null);

  function handleBack() {
    if (location.state && typeof location.state === 'object' && 'fromLibrary' in location.state) {
      navigate(-1);
      return;
    }
    navigate('/marketing/content/website');
  }

  if (!contentItemId) {
    return <Alert variant="error">Missing content item ID.</Alert>;
  }

  if (itemQuery.isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-8">
        <Card className="p-6 flex items-center gap-3">
          <Spinner />
          <span className="text-body-sm text-text-secondary">Loading website review…</span>
        </Card>
      </div>
    );
  }

  if (itemQuery.error instanceof Error) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-8 space-y-4">
        <Button variant="ghost" size="sm" onClick={handleBack}>Back</Button>
        <Alert variant="error">{itemQuery.error.message}</Alert>
      </div>
    );
  }

  const item = itemQuery.data;
  if (!item) return null;

  const meta = (item.metadata as Record<string, unknown> | undefined) ?? {};

  // LinkedIn posts use their own review panel (edit-in-your-words + "Finished"
  // saves the text as a voice sample). They never carry websiteRefresh metadata.
  if (item.type === 'linkedin_post') {
    return <LinkedInReviewPanel item={item} />;
  }

  const websiteRefresh = meta.websiteRefresh as Record<string, unknown> | undefined;
  if (!websiteRefresh) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-8 space-y-4">
        <Button variant="ghost" size="sm" onClick={handleBack}>Back</Button>
        <Alert variant="error">This content item is not a website review item.</Alert>
      </div>
    );
  }

  if (meta.generating) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-8 space-y-4">
        <Button variant="ghost" size="sm" onClick={handleBack}>Back</Button>
        <Card className="p-6 flex items-center gap-3">
          <Spinner />
          <div className="space-y-1">
            <div className="text-body-sm font-medium text-text-primary">Finalising website draft…</div>
            <p className="text-body-xs text-text-secondary">
              The content, scoring, and saved review explanation are still being completed. Review will open once the draft is fully ready.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  const sourceUrl = typeof websiteRefresh.sourceUrl === 'string' ? websiteRefresh.sourceUrl : item.externalRef;
  const routeFamily = typeof websiteRefresh.routeFamily === 'string' ? websiteRefresh.routeFamily : null;
  const sentToSanityDraft = !!websiteRefresh.lastPushedAt;
  const refreshMode =
    websiteRefresh.refreshMode === 'scratch'
    || websiteRefresh.refreshMode === 'rewrite'
    || websiteRefresh.refreshMode === 'manual'
      ? (websiteRefresh.refreshMode as 'scratch' | 'rewrite' | 'manual')
      : null;
  const studioDraftUrl = reviewQuery.data?.studioDraftUrl ?? null;
  const score = reviewQuery.data?.score ?? ((meta.score as Record<string, number> | undefined) ?? null);
  const transparency = reviewQuery.data?.transparency ?? null;
  const transparencyError = reviewQuery.data?.transparencyError ?? null;
  const selectedStatus = normalizeWebsiteReviewStatus(item.status);
  const seoKeywords = extractSeoKeywords(meta.seoKeyword);
  const previousScoreSnapshot = extractPreviousScoreSnapshot(meta.scoreHistory);
  const optimizationReport = (websiteRefresh.optimizationReport ?? null) as OptimizationReport | null;

  function handleDeleteRule(ruleId: string) {
    if (!window.confirm('Delete this reusable generation rule?')) return;
    setDeletingRuleId(ruleId);
    deleteFeedbackRule.mutate(ruleId, {
      onSuccess: () => {
        void modificationsQuery.refetch();
      },
      onSettled: () => {
        setDeletingRuleId(null);
      },
    });
  }

  function handleDeleteModification(modificationId: string) {
    if (!window.confirm('Remove this rule-analysis entry from the review page?')) return;
    setDeletingModificationId(modificationId);
    deleteModification.mutate(
      { modificationId, contentItemId: contentItemId! },
      {
        onSuccess: () => {
          void modificationsQuery.refetch();
        },
        onSettled: () => {
          setDeletingModificationId(null);
        },
      },
    );
  }

  async function handleRestoreVersion(version: ContentVersion) {
    const confirmed = window.confirm(
      `Restore saved version v${version.versionNum}? The current page state will be snapshotted first so you can undo the restore if needed.`,
    );
    if (!confirmed) return;
    try {
      await restoreVersion.mutateAsync({ contentItemId: contentItemId!, versionId: version.id });
      setVersionPreview(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to restore saved version';
      window.alert(message);
    }
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={handleBack}>
          Back
        </Button>
        <div className="flex items-center gap-2">
          <label htmlFor="website-review-status" className="text-body-xs font-medium text-text-secondary">
            Status
          </label>
          <Select
            id="website-review-status"
            value={selectedStatus}
            onChange={(event) => {
              patchItem.mutate({
                id: item.id,
                updates: { status: event.target.value as WebsiteReviewStatus },
              });
            }}
            disabled={patchItem.isPending}
            className="min-w-[150px]"
          >
            <option value="draft">Draft</option>
            <option value="review">In review</option>
            <option value="approved">Approved</option>
          </Select>
        </div>
      </div>

      <ReviewActionSection item={item} />

      <div className="space-y-3">
        <div className="space-y-3 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="default">{TYPE_LABELS[item.type] ?? item.type}</Badge>
            <Badge variant={getDisplayStatusVariant(item)}>{getDisplayStatusLabel(item)}</Badge>
            <Badge variant="default">{item.language.toUpperCase()}</Badge>
            <Badge variant="default">Website refresh</Badge>
            {refreshMode === 'scratch' && <Badge variant="default">From scratch</Badge>}
            {refreshMode === 'rewrite' && <Badge variant="default">From live page</Badge>}
            {refreshMode === 'manual' && <Badge variant="default">Manual import</Badge>}
            {routeFamily && <Badge variant="default">{routeFamily}</Badge>}
            {sentToSanityDraft && <Badge variant="default">Sanity draft sent</Badge>}
          </div>
          <div className="space-y-1">
            <h1 className="text-display-sm text-text-primary">{displayTitle(item.title)}</h1>
            <p className="text-body-sm text-text-secondary">
              Review the current live page against the proposed update, refine the copy section by section, then push the reviewed draft to Sanity.
            </p>
          </div>

          <Card className="p-4 min-w-[240px] space-y-3">
            <div className="space-y-1">
              <h2 className="text-body-sm font-medium text-text-primary">Scores</h2>
              <p className="text-body-xs text-text-tertiary">
                Based on the current proposed snapshot. Semantic coverage uses the SEO keyword from Notion plus extracted SERP entities.
              </p>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
              <ScorePill
                label="SEO"
                value={score?.seo_score}
                previousValue={previousScoreSnapshot?.scores.seo_score}
                previousAt={previousScoreSnapshot?.recordedAt}
                tone="blue"
              />
              <ScorePill
                label="GEO"
                value={score?.geo_score}
                previousValue={previousScoreSnapshot?.scores.geo_score}
                previousAt={previousScoreSnapshot?.recordedAt}
                tone="green"
              />
              <ScorePill
                label="Semantic"
                value={typeof score?.semantic_coverage === 'number' ? score.semantic_coverage : null}
                previousValue={previousScoreSnapshot?.scores.semantic_coverage}
                previousAt={previousScoreSnapshot?.recordedAt}
                tone="purple"
                hint={
                  typeof score?.semantic_coverage === 'number'
                    ? 'Cosine similarity between the article and a SERP target embedding (keyword + extracted entities), mapped to 1-10.'
                    : (typeof meta.semanticSkipReason === 'string' ? meta.semanticSkipReason : 'Semantic coverage not yet computed for this item.')
                }
              />
              <ScorePill
                label="Overall"
                value={score?.overall_score}
                previousValue={previousScoreSnapshot?.scores.overall_score}
                previousAt={previousScoreSnapshot?.recordedAt}
                tone="neutral"
              />
            </div>
            {recalculateScore.data?.keywordChanged && (
              <Alert variant="warning">
                <span className="text-body-xs">
                  SEO keyword in Notion changed since this content was generated:{' '}
                  <strong>{recalculateScore.data.keywordChanged.from ?? '(none)'}</strong> →{' '}
                  <strong>{recalculateScore.data.keywordChanged.to ?? '(none)'}</strong>. Re-run SERP analysis to refresh entities for the new keyword.
                </span>
              </Alert>
            )}
            {Array.isArray(meta.keywordWarnings) && (meta.keywordWarnings as string[]).length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 space-y-1">
                <div className="text-body-2xs font-medium uppercase tracking-wide text-amber-800">Keyword guard</div>
                <ul className="list-disc pl-5 space-y-0.5 text-body-xs text-amber-900">
                  {(meta.keywordWarnings as string[]).map((warning, idx) => (
                    <li key={idx}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}
            {typeof meta.semanticSkipReason === 'string' && meta.semanticSkipReason && typeof score?.semantic_coverage !== 'number' && (
              <p className="text-body-xs text-text-tertiary italic">{meta.semanticSkipReason}</p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => recalculateScore.mutate(item.id)}
                disabled={recalculateScore.isPending}
              >
                {recalculateScore.isPending ? 'Recalculating...' : 'Recalculate scores'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={async () => {
                  setSerpFeedback(null);
                  try {
                    const result = await refreshSerp.mutateAsync(item.id);
                    setSerpFeedback({
                      kind: 'success',
                      message: `SERP entities refreshed (${result.entityCount} entities from ${result.competitorPagesCount} competitor pages). Click "Recalculate scores" to update Semantic.`,
                    });
                  } catch (error) {
                    setSerpFeedback({
                      kind: 'error',
                      message: error instanceof Error ? error.message : 'Failed to refresh SERP entities',
                    });
                  }
                }}
                disabled={refreshSerp.isPending}
                title="Refreshes competitor entities for Semantic scoring. Does not change page text."
              >
                {refreshSerp.isPending ? 'Re-running SERP...' : 'Re-run SERP analysis'}
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={() => setRegenDialogOpen(true)}
                disabled={generateRefresh.isPending || !!meta.generating}
              >
                {generateRefresh.isPending ? 'Starting...' : 'Re-generate'}
              </Button>
            </div>
            {recalculateScore.isError && (
              <Alert variant="error">
                {recalculateScore.error instanceof Error ? recalculateScore.error.message : 'Failed to recalculate scores'}
              </Alert>
            )}
            {serpFeedback && (
              <Alert variant={serpFeedback.kind === 'success' ? 'info' : 'error'}>
                <span className="text-body-xs">{serpFeedback.message}</span>
              </Alert>
            )}
          </Card>

          {optimizationReport && <OptimizationReportPanel report={optimizationReport} />}

          {seoKeywords.length > 0 && (
            <Card className="p-4 space-y-3 bg-blue-50/50 border-blue-100">
              <div className="space-y-1">
                <h2 className="text-body-sm font-medium text-text-primary">Target keywords</h2>
                <p className="text-body-xs text-text-tertiary">
                  From the Website Content Notion brief. Keep these terms naturally covered while reviewing the page.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {seoKeywords.map((keyword) => (
                  <Badge key={keyword} variant="default">
                    {keyword}
                  </Badge>
                ))}
              </div>
            </Card>
          )}

          {transparency ? (
            <TransparencyCard transparency={transparency} />
          ) : (
            <Card className="p-4 space-y-3">
              <div className="space-y-1">
                <h2 className="text-body-sm font-medium text-text-primary">How the engine built this draft</h2>
                <p className="text-body-xs text-text-tertiary">
                  This explanation is generated once when the website draft is created and then saved with the item for later review.
                </p>
              </div>
              {transparencyError ? (
                <Alert variant="error">
                  Generating the explanation failed: {transparencyError}
                </Alert>
              ) : (
                <Alert variant="info">
                  No saved generation explanation is available for this draft yet.
                </Alert>
              )}
            </Card>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-body-xs text-text-secondary">
          {sourceUrl && (
            <a href={sourceUrl} target="_blank" rel="noreferrer" className="underline underline-offset-2">
              Open live page
            </a>
          )}
          {studioDraftUrl && (
            <a href={studioDraftUrl} target="_blank" rel="noreferrer" className="underline underline-offset-2">
              Open draft in Sanity
            </a>
          )}
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-6">
          {/* Renders nothing unless this item went through the impact pipeline. */}
          <VerificationPanel contentItemId={item.id} />
          <WebsiteReviewPanel contentItemId={item.id} />
        </div>

        <div className="space-y-4">
          <Card className="p-4 space-y-3">
            <div className="space-y-1">
              <h2 className="text-body-md font-medium text-text-primary">Version history</h2>
              <p className="text-body-xs text-text-tertiary">
                Every generation run, edit pass, and draft push is saved so this page can be reopened and reviewed later.
              </p>
            </div>

            {versionsQuery.isLoading && (
              <div className="flex items-center gap-2 text-body-xs text-text-secondary">
                <Spinner className="w-4 h-4" />
                Loading history…
              </div>
            )}

            {versionsQuery.data?.versions.length === 0 && (
              <p className="text-body-xs text-text-secondary">No saved history yet.</p>
            )}

            <div className="space-y-2">
              {versionsQuery.data?.versions.map((version) => (
                <button
                  key={version.id}
                  type="button"
                  className="w-full rounded-xl border border-border bg-surface-subtle px-3 py-2 text-left space-y-1 hover:border-border-strong"
                  onClick={() => setVersionPreview(version)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-body-xs font-medium text-text-primary">v{version.versionNum}</span>
                    <span className="text-body-xs text-text-tertiary">
                      {formatDateTime(version.createdAt)}
                    </span>
                  </div>
                  <div className="text-body-xs text-text-secondary">
                    <span className="font-mono">{version.editSource}</span>
                  </div>
                  {version.diffSummary && (
                    <p className="text-body-xs text-text-secondary">{version.diffSummary}</p>
                  )}
                </button>
              ))}
            </div>

            {versionPreview && (
              <div className="border-t border-border pt-3 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1 min-w-0">
                    <p className="text-body-xs font-medium text-text-primary">
                      v{versionPreview.versionNum} preview: {versionPreview.title}
                    </p>
                    <p className="text-body-xs text-text-tertiary">
                      {formatDateTime(versionPreview.createdAt)} · <span className="font-mono">{versionPreview.editSource}</span>
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    isLoading={restoreVersion.isPending && restoreVersion.variables?.versionId === versionPreview.id}
                    onClick={() => {
                      void handleRestoreVersion(versionPreview);
                    }}
                  >
                    Restore
                  </Button>
                </div>
                {versionPreview.diffSummary && (
                  <p className="text-body-xs text-text-secondary">{versionPreview.diffSummary}</p>
                )}
                <div className="max-h-[30vh] overflow-y-auto whitespace-pre-wrap rounded-xl border border-border bg-surface-subtle p-3 text-body-xs text-text-secondary">
                  {versionPreview.body || '(empty)'}
                </div>
              </div>
            )}
          </Card>

          <Card className="p-4 space-y-3">
            <div className="space-y-1">
              <h2 className="text-body-md font-medium text-text-primary">Rule analysis</h2>
              <p className="text-body-xs text-text-tertiary">
                Each saved review change is checked to see whether it should become reusable website guidance for future generations.
              </p>
            </div>

            {modificationsQuery.isLoading && (
              <div className="flex items-center gap-2 text-body-xs text-text-secondary">
                <Spinner className="w-4 h-4" />
                Loading rule-analysis history…
              </div>
            )}

            {modificationsQuery.data?.modifications.length === 0 && (
              <p className="text-body-xs text-text-secondary">No review modifications captured yet.</p>
            )}

            <div className="space-y-3">
              {modificationsQuery.data?.modifications.map((modification) => (
                <div
                  key={modification.id}
                  className={`rounded-2xl border px-4 py-4 space-y-3 ${modificationCardTone(modification.extractionStatus)}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-2 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="pill">{modificationSourceLabel(modification.source)}</Badge>
                        <Badge variant={modificationStatusVariant(modification.extractionStatus)}>
                          {modificationStatusLabel(modification.extractionStatus)}
                        </Badge>
                      </div>
                      <div className="text-body-sm font-medium text-text-primary">
                        {modification.diffSummary || 'Reviewed website change'}
                      </div>
                      <div className="text-body-2xs uppercase tracking-[0.12em] text-text-tertiary">
                        {formatDateTime(modification.detectedAt)}
                      </div>
                    </div>
                  </div>

                  {modification.sectionsChanged.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {modification.sectionsChanged.map((section) => (
                        <Badge key={section} variant="default">
                          {humanizeSectionLabel(section)}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {modification.rule && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-3 py-3 text-body-xs text-emerald-950">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="font-medium text-emerald-900">
                          {modification.rule.status === 'pending'
                            ? 'Reusable rule candidate'
                            : 'Reusable generation rule'}
                        </div>
                        <Badge variant="success">
                          {modification.rule.status === 'pending'
                            ? 'Candidate saved'
                            : modification.extractionStatus === 'updated_existing'
                              ? 'Rule refined'
                              : 'Rule created'}
                        </Badge>
                      </div>
                      <p>{modification.rule.rule}</p>
                      <div className="mt-3 flex justify-end">
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteRule(modification.rule!.id)}
                          disabled={deleteFeedbackRule.isPending}
                        >
                          {deletingRuleId === modification.rule.id ? 'Deleting...' : 'Delete rule'}
                        </Button>
                      </div>
                      {modification.rule.status === 'pending' && (
                        <p className="mt-2 text-body-2xs text-emerald-900/80">
                          This rule candidate will be checked against existing Generation Rules and promoted only when the reviewed draft is pushed to Sanity.
                        </p>
                      )}
                    </div>
                  )}

                  {!modification.rule && (modification.extractionStatus === 'completed' || modification.extractionStatus === 'updated_existing') && (
                    <div className="rounded-xl border border-border-light bg-surface-primary px-3 py-3 text-body-xs text-text-secondary">
                      <div className="font-medium text-text-primary mb-1">Rule removed</div>
                      <p>This reusable generation rule was deleted from the review page and is no longer active in Generation Rules.</p>
                      <div className="mt-3 flex justify-end">
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteModification(modification.id)}
                          disabled={deleteModification.isPending}
                        >
                          {deletingModificationId === modification.id ? 'Removing...' : 'Remove entry'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {!modification.rule && modification.extractionStatus === 'duplicate' && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-3 text-body-xs text-amber-950">
                      <div className="font-medium text-amber-900 mb-1">No new rule added</div>
                      <p>This edit matched guidance the engine already knows, so it was skipped instead of creating a duplicate rule.</p>
                      <div className="mt-3 flex justify-end">
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteModification(modification.id)}
                          disabled={deleteModification.isPending}
                        >
                          {deletingModificationId === modification.id ? 'Removing...' : 'Remove entry'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {!modification.rule && modification.extractionStatus === 'no_rule' && (
                    <div className="rounded-xl border border-border-light bg-surface-primary px-3 py-3 text-body-xs text-text-secondary">
                      <div className="font-medium text-text-primary mb-1">No reusable rule</div>
                      <p>This change was saved, but it was too page-specific to turn into general website guidance.</p>
                      <div className="mt-3 flex justify-end">
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteModification(modification.id)}
                          disabled={deleteModification.isPending}
                        >
                          {deletingModificationId === modification.id ? 'Removing...' : 'Remove entry'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {!modification.rule && modification.extractionStatus === 'pending' && (
                    <div className="rounded-xl border border-blue-200 bg-blue-50/70 px-3 py-3 text-body-xs text-blue-950">
                      <div className="font-medium text-blue-900 mb-1">Rule analysis running</div>
                      <p>The engine is reviewing this saved change to decide whether it should become reusable guidance.</p>
                      <div className="mt-3 flex justify-end">
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteModification(modification.id)}
                          disabled={deleteModification.isPending}
                        >
                          {deletingModificationId === modification.id ? 'Removing...' : 'Remove entry'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {!modification.rule && modification.extractionStatus === 'failed' && modification.extractionError && (
                    <div className="space-y-3">
                      <Alert variant="error">{modification.extractionError}</Alert>
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteModification(modification.id)}
                          disabled={deleteModification.isPending}
                        >
                          {deletingModificationId === modification.id ? 'Removing...' : 'Remove entry'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {regenDialogOpen && (
        <RegenerateDialog
          isOpen
          itemTitle={item.title}
          isPending={generateRefresh.isPending}
          onClose={() => setRegenDialogOpen(false)}
          onConfirm={async (input) => {
            setRegenDialogOpen(false);
            try {
              await generateRefresh.mutateAsync({ id: item.id, input });
              navigate('/marketing/content');
            } catch (error) {
              const message = error instanceof Error ? error.message : 'Failed to start re-generation';
              window.alert(message);
            }
          }}
        />
      )}
    </div>
  );
}

function TransparencyCard({
  transparency,
}: {
  transparency: WebsiteReviewTransparency;
}) {
  return (
    <Card className="p-4 space-y-4">
      <div className="space-y-1">
        <h2 className="text-body-sm font-medium text-text-primary">How the engine built this draft</h2>
        <p className="text-body-xs text-text-tertiary">
          LLM-generated review notes based on the current draft, the scoring result, and the sources used during generation.
        </p>
      </div>

      <div className="space-y-3">
        <TransparencyBlock title="What changed" body={transparency.whatChanged} />
        <TransparencyBlock title="How it was optimized" body={transparency.optimizationFocus} />
        <TransparencyBlock title="How the score should be read" body={transparency.scoreExplanation} />
      </div>

      {transparency.sourceUsage.length > 0 && (
        <div className="space-y-2">
          <div className="text-body-xs font-medium uppercase tracking-[0.12em] text-text-tertiary">
            How the sources were used
          </div>
          <div className="space-y-2">
            {transparency.sourceUsage.map((entry) => (
              <div key={`${entry.label}-${entry.detail}`} className="rounded-xl border border-border bg-surface-subtle px-3 py-3 space-y-1">
                <div className="text-body-xs font-medium text-text-primary">{entry.label}</div>
                <p className="text-body-xs text-text-secondary">{entry.detail}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {transparency.howToImprove.length > 0 && (
        <div className="space-y-2">
          <div className="text-body-xs font-medium uppercase tracking-[0.12em] text-text-tertiary">
            How to push the score higher
          </div>
          <ul className="space-y-2 text-body-xs text-text-secondary">
            {transparency.howToImprove.map((tip) => (
              <li key={tip} className="rounded-xl border border-border bg-surface-subtle px-3 py-3">
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function TransparencyBlock({
  title,
  body,
}: {
  title: string;
  body: string;
}) {
  return (
    <div className="space-y-1">
      <div className="text-body-xs font-medium uppercase tracking-[0.12em] text-text-tertiary">
        {title}
      </div>
      <p className="text-body-sm text-text-secondary leading-6">{body}</p>
    </div>
  );
}

function modificationStatusLabel(
  status: 'pending' | 'completed' | 'updated_existing' | 'duplicate' | 'no_rule' | 'failed',
): string {
  if (status === 'pending') return 'Analysing';
  if (status === 'completed') return 'Rule created';
  if (status === 'updated_existing') return 'Rule refined';
  if (status === 'duplicate') return 'Duplicate';
  if (status === 'failed') return 'Analysis failed';
  return 'No rule';
}

function modificationStatusVariant(
  status: 'pending' | 'completed' | 'updated_existing' | 'duplicate' | 'no_rule' | 'failed',
): 'default' | 'info' | 'success' | 'warning' | 'error' {
  if (status === 'pending') return 'info';
  if (status === 'completed' || status === 'updated_existing') return 'success';
  if (status === 'duplicate') return 'warning';
  if (status === 'failed') return 'error';
  return 'default';
}

function modificationCardTone(
  status: 'pending' | 'completed' | 'updated_existing' | 'duplicate' | 'no_rule' | 'failed',
): string {
  if (status === 'completed' || status === 'updated_existing') {
    return 'border-emerald-200 bg-emerald-50/30';
  }
  if (status === 'duplicate') {
    return 'border-amber-200 bg-amber-50/30';
  }
  if (status === 'pending') {
    return 'border-blue-200 bg-blue-50/30';
  }
  if (status === 'failed') {
    return 'border-red-200 bg-red-50/30';
  }
  return 'border-border bg-surface-subtle';
}

function modificationSourceLabel(source: 'rewrite_push' | 'studio_poll' | 'manual'): string {
  if (source === 'manual') return 'Saved edit';
  if (source === 'studio_poll') return 'Sanity sync';
  return 'Draft push';
}

function humanizeSectionLabel(section: string): string {
  return section
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

interface PreviousScoreSnapshot {
  recordedAt: string | null;
  scores: {
    seo_score?: number;
    geo_score?: number;
    semantic_coverage?: number;
    overall_score?: number;
  };
}

function extractPreviousScoreSnapshot(rawHistory: unknown): PreviousScoreSnapshot | null {
  if (!Array.isArray(rawHistory)) return null;
  // Find the most recent entry that has the new full per-dim breakdown.
  // The latest entry corresponds to the *current* score; the one before is "previous".
  const entriesWithScores: Array<{ scores: Record<string, number>; recordedAt: string | null }> = [];
  for (const raw of rawHistory) {
    if (!raw || typeof raw !== 'object') continue;
    const entry = raw as Record<string, unknown>;
    const scoresRaw = entry.scores;
    if (!scoresRaw || typeof scoresRaw !== 'object') continue;
    const scoresEntries = Object.entries(scoresRaw as Record<string, unknown>)
      .filter(([, v]) => typeof v === 'number') as Array<[string, number]>;
    if (scoresEntries.length === 0) continue;
    const recordedAt = typeof entry.recordedAt === 'string'
      ? entry.recordedAt
      : (typeof entry.recalculatedAt === 'string' ? entry.recalculatedAt : null);
    entriesWithScores.push({
      scores: Object.fromEntries(scoresEntries),
      recordedAt,
    });
  }
  if (entriesWithScores.length < 2) return null;
  const previous = entriesWithScores[entriesWithScores.length - 2]!;
  return {
    recordedAt: previous.recordedAt,
    scores: previous.scores,
  };
}

function formatPreviousLabel(recordedAt: string | null | undefined): string {
  if (!recordedAt) return 'previous recalc';
  const d = new Date(recordedAt);
  if (Number.isNaN(d.getTime())) return 'previous recalc';
  return `vs. ${formatDayMonthTime(d)}`;
}

function ScorePill({
  label,
  value,
  previousValue,
  previousAt,
  tone,
  hint,
}: {
  label: string;
  value: number | null | undefined;
  previousValue?: number | null | undefined;
  previousAt?: string | null | undefined;
  tone: 'blue' | 'green' | 'neutral' | 'purple';
  hint?: string;
}) {
  const toneClasses = tone === 'blue'
    ? 'bg-blue-50 text-blue-700'
    : tone === 'green'
      ? 'bg-emerald-50 text-emerald-700'
      : tone === 'purple'
        ? 'bg-violet-50 text-violet-700'
        : 'bg-surface-subtle text-text-primary';

  const hasDelta = typeof value === 'number' && typeof previousValue === 'number';
  const delta = hasDelta ? Math.round((value - previousValue) * 10) / 10 : 0;
  const deltaArrow = !hasDelta ? '' : delta > 0 ? '↑' : delta < 0 ? '↓' : '–';
  const deltaColor = !hasDelta
    ? ''
    : delta > 0
      ? 'text-emerald-600'
      : delta < 0
        ? 'text-rose-600'
        : 'text-text-tertiary';
  const deltaLabel = hasDelta
    ? `${formatPreviousLabel(previousAt)}: ${previousValue.toFixed(1)} → ${value.toFixed(1)} (${delta > 0 ? '+' : ''}${delta.toFixed(1)})`
    : undefined;

  return (
    <div className={`rounded-xl px-3 py-2 ${toneClasses}`} title={hint}>
      <div className="text-body-2xs uppercase tracking-wide">{label}</div>
      <div className="text-body-sm font-semibold">
        {typeof value === 'number' ? value.toFixed(1) : '--'}
      </div>
      {hasDelta && (
        <div className={`text-body-2xs font-medium ${deltaColor}`} title={deltaLabel}>
          {deltaArrow} {delta > 0 ? '+' : ''}{delta.toFixed(1)}
        </div>
      )}
    </div>
  );
}

function formatScoreDelta(before: number, after: number): { text: string; tone: 'positive' | 'negative' | 'neutral' } {
  const delta = Math.round((after - before) * 10) / 10;
  if (delta > 0) return { text: `+${delta.toFixed(1)}`, tone: 'positive' };
  if (delta < 0) return { text: `${delta.toFixed(1)}`, tone: 'negative' };
  return { text: '±0.0', tone: 'neutral' };
}

const SCORE_DELTA_ROWS: Array<{ key: keyof OptimizationReport['scoreDeltas']; label: string }> = [
  { key: 'overall_score', label: 'Overall' },
  { key: 'seo', label: 'SEO' },
  { key: 'geo', label: 'GEO (LLM)' },
  { key: 'geo_citability', label: 'GEO citability' },
  { key: 'semantic_coverage', label: 'Semantic coverage' },
  { key: 'icp_relevance', label: 'ICP relevance' },
  { key: 'data_richness', label: 'Data richness' },
  { key: 'readability_score', label: 'Readability' },
  { key: 'internal_link_score', label: 'Internal links' },
];

function OptimizationReportPanel({ report }: { report: OptimizationReport }) {
  const overallDelta = formatScoreDelta(
    report.scoreDeltas.overall_score.before,
    report.scoreDeltas.overall_score.after,
  );
  return (
    <Card className="p-4 space-y-4">
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="text-body-sm font-medium text-text-primary">SEO/GEO optimization report</h2>
          <Badge variant={report.passCount > 0 ? 'info' : 'default'}>
            {report.passCount} of {report.maxFixPasses} fix passes accepted
          </Badge>
        </div>
        <p className="text-body-xs text-text-tertiary">
          What the optimization loop changed. Generated when the page is regenerated in "Optimize for SEO and GEO" mode.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1">
          <div className="text-body-2xs text-text-tertiary uppercase tracking-wide">Overall score</div>
          <div className="text-display-sm text-text-primary">
            {report.scoreDeltas.overall_score.after.toFixed(1)}
          </div>
          <div className={
            overallDelta.tone === 'positive' ? 'text-body-xs text-green-700'
            : overallDelta.tone === 'negative' ? 'text-body-xs text-red-700'
            : 'text-body-xs text-text-tertiary'
          }>
            {overallDelta.text} vs. before the optimization
          </div>
        </div>
        <div className="md:col-span-2 space-y-1">
          <div className="text-body-2xs text-text-tertiary uppercase tracking-wide">Per-dimension deltas</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {SCORE_DELTA_ROWS.map(({ key, label }) => {
              const delta = report.scoreDeltas[key];
              if (!delta) return null;
              const formatted = formatScoreDelta(delta.before, delta.after);
              return (
                <div key={key} className="flex items-center justify-between gap-2 text-body-xs">
                  <span className="text-text-secondary">{label}</span>
                  <span className="font-medium text-text-primary">
                    {delta.after.toFixed(1)}{' '}
                    <span className={
                      formatted.tone === 'positive' ? 'text-green-700'
                      : formatted.tone === 'negative' ? 'text-red-700'
                      : 'text-text-tertiary'
                    }>
                      ({formatted.text})
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {report.passes.length > 0 && (
        <div className="space-y-1">
          <div className="text-body-2xs text-text-tertiary uppercase tracking-wide">Pass log</div>
          <ul className="space-y-1">
            {report.passes.map((p) => (
              <li key={p.pass} className="text-body-xs text-text-secondary flex items-center gap-2 flex-wrap">
                <Badge variant={p.accepted ? 'info' : 'default'}>Pass {p.pass}</Badge>
                <span>
                  {p.accepted ? 'accepted' : 'rejected'}
                  {p.acceptanceRule ? ` (${p.acceptanceRule === 'hard_gap_closed' ? 'gap closed' : 'overall improvement'})` : ''}
                  {' '}— hard gaps {p.hardGapsBefore} → {p.hardGapsAfter}, overall {p.overallBefore.toFixed(1)} → {p.overallAfter.toFixed(1)}
                  {typeof p.citabilityBefore === 'number' && typeof p.citabilityAfter === 'number'
                    ? `, citability ${p.citabilityBefore.toFixed(1)} → ${p.citabilityAfter.toFixed(1)}`
                    : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.gapsClosed.length > 0 && (
        <div className="space-y-1">
          <div className="text-body-2xs text-text-tertiary uppercase tracking-wide">Gaps closed</div>
          <ul className="space-y-1">
            {report.gapsClosed.map((g, i) => (
              <li key={i} className="text-body-xs text-green-700">✓ {g}</li>
            ))}
          </ul>
        </div>
      )}

      {report.gapsOpen.length > 0 && (
        <div className="space-y-1">
          <div className="text-body-2xs text-text-tertiary uppercase tracking-wide">Gaps still open</div>
          <ul className="space-y-1">
            {report.gapsOpen.map((g, i) => (
              <li key={i} className="text-body-xs text-amber-700">! {g}</li>
            ))}
          </ul>
        </div>
      )}

      {report.weakestSectionsAtEnd.length > 0 && (
        <div className="space-y-1">
          <div className="text-body-2xs text-text-tertiary uppercase tracking-wide">Weakest sections (semantic coverage)</div>
          <ul className="space-y-1">
            {report.weakestSectionsAtEnd.map((s) => (
              <li key={s.heading} className="text-body-xs text-text-secondary">
                <span className="font-medium text-text-primary">{s.heading}</span>: semantic {s.rawScore.toFixed(3)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function ReviewActionSection({ item }: { item: { id: string; status: string; metadata: unknown } }) {
  const meta = (item.metadata as Record<string, unknown> | null) ?? {};
  const reviewMeta = meta.review as ReviewMeta | undefined;
  const hasActiveQueue = item.status === 'review' && !!reviewMeta?.reviewers?.length;
  const wasReviewed = !!reviewMeta?.reviewers?.some((r) => r.status === 'approved');

  // Active sequential queue — show progress dots, no picker.
  if (hasActiveQueue) {
    const currentIndex = reviewMeta!.currentIndex ?? 0;
    return (
      <Card className="p-4 space-y-3">
        <div className="space-y-1">
          <h2 className="text-body-md font-medium text-text-primary">Review in progress</h2>
          <p className="text-body-xs text-text-tertiary">
            Each reviewer gets a Slack DM with a link to OS. They make their edits here and click
            "Mark as reviewed": the next reviewer is DM'd automatically.
          </p>
        </div>
        <ol className="space-y-2">
          {reviewMeta!.reviewers.map((r, idx) => {
            const isCurrent = idx === currentIndex && r.status === 'pending';
            const isApproved = r.status === 'approved';
            const isQueued = idx > currentIndex;
            return (
              <li
                key={r.userId}
                className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${
                  isApproved
                    ? 'border-emerald-200 bg-emerald-50/50'
                    : isCurrent
                      ? 'border-blue-300 bg-blue-50/60'
                      : 'border-border bg-surface-subtle'
                }`}
              >
                <span
                  className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-semibold ${
                    isApproved
                      ? 'bg-emerald-200 text-emerald-800'
                      : isCurrent
                        ? 'bg-blue-200 text-blue-800'
                        : 'bg-surface-secondary text-text-tertiary'
                  }`}
                >
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-body-sm font-medium text-text-primary">{r.name}</div>
                  <div className="text-body-xs text-text-tertiary">
                    {isApproved && r.approvedAt
                      ? `Reviewed ${formatDateTime(r.approvedAt)}`
                      : isCurrent
                        ? 'Currently reviewing: Slack DM sent, will mark as reviewed in OS'
                        : isQueued
                          ? 'Waiting in queue'
                          : 'Pending'}
                  </div>
                </div>
                <span className="text-body-xs">
                  {isApproved ? '✅' : isCurrent ? '🕓' : '·'}
                </span>
              </li>
            );
          })}
        </ol>
        {reviewMeta!.note && (
          <div className="text-body-xs text-text-secondary border-l-2 border-blue-200 pl-3 italic">
            "{reviewMeta!.note}"
          </div>
        )}
      </Card>
    );
  }

  // Any other status — surface the picker. Even items in `review` without a
  // queue (legacy drop-down flips), `approved` (re-review for another round),
  // or `published` (rare but possible) get the action exposed so Walter is
  // never stuck without a button.
  const headlineByStatus: Record<string, string> = {
    draft: 'Send to review',
    review: 'Send to review',
    approved: 'Send for another round of review',
    published: 'Send for review',
  };
  const headline = headlineByStatus[item.status] ?? 'Send to review';
  const subhint =
    item.status === 'approved'
      ? `Already approved${wasReviewed ? ` by ${reviewMeta!.reviewers.filter((r) => r.status === 'approved').map((r) => r.name).join(', ')}` : ''}. You can still queue another review pass before pushing to Sanity.`
      : item.status === 'review'
        ? 'This item is in review status but has no Slack queue stamped yet. Choose reviewers below to start the sequential flow.'
        : item.status === 'published'
          ? 'This page is already published. You can still queue a review pass against the current draft.'
          : 'Choose reviewers in order. Each one gets a Slack DM with a link to OS, makes their edits here, and clicks "Mark as reviewed": the next reviewer is DM\'d automatically once they do.';

  return (
    <Card className={`p-4 space-y-3 ${item.status === 'approved' ? 'border-emerald-200 bg-emerald-50/30' : ''}`}>
      <div className="space-y-1">
        <h2 className="text-body-md font-medium text-text-primary">{headline}</h2>
        <p className="text-body-xs text-text-tertiary">{subhint}</p>
      </div>
      <ReviewerPicker itemId={item.id} />
    </Card>
  );
}
